import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { SpeciaRing } from "@/components/SpeciaLogo";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, AlertTriangle, Loader2, ShieldCheck } from "lucide-react";

const STATUS = { passed: ["#0F9D77", CheckCircle2], failed: ["#E5325F", XCircle], blocked: ["#FF8A3D", AlertTriangle] };

export default function SharedReport() {
  const { token } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/shared/${token}`).then((x) => setR(x.data)).catch(() => setErr("This report link is invalid or has been revoked."));
  }, [token]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-secondary" data-testid="shared-report-error">{err}</div>;
  if (!r) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#3A2DB8]" /></div>;
  const ok = r.release_ready;

  return (
    <div className="min-h-screen" data-testid="shared-report-page">
      <header className="border-b border-border bg-white/70 glass">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center gap-3">
          <SpeciaRing size={28} /><span className="font-heading font-bold text-[#2A1E9E] text-xl lowercase">specia</span>
          <span className="h-5 w-px bg-border" /><span className="neon-word text-sm">TRINITY</span>
          <span className="ml-auto text-xs text-secondary">Shared release-readiness report</span>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-12">
        <p className="overline text-[#7C3AED] mb-2">{r.product === "sap_successfactors" ? "SAP SuccessFactors" : "SAP Public Cloud"} · {r.trigger} run · {r.started_at?.slice(0, 16).replace("T", " ")} UTC</p>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#1E1560]" data-testid="shared-env-name">{r.environment_name}</h1>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-white font-semibold" style={{ background: ok ? "#0F9D77" : "#FF8A3D" }} data-testid="shared-verdict">
            <ShieldCheck className="w-4 h-4" /> {ok ? "Release ready" : "Needs attention"} — {r.pass_rate}% pass
          </span>
          <span className="text-sm text-secondary">{r.passed} passed · {r.failed} failed · {r.blocked} blocked · {r.total} tests · {((r.duration_ms || 0) / 1000).toFixed(0)}s</span>
        </div>
        <div className="mt-8 card-soft divide-y divide-border">
          {r.results.map((x, i) => {
            const [c, Icon] = STATUS[x.status] || STATUS.blocked;
            return (
              <div key={`${x.name}-${i}`} className="p-4 flex items-start gap-3" data-testid={`shared-result-${i}`}>
                <Icon className="w-5 h-5 shrink-0 mt-0.5" style={{ color: c }} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#1E1560]">{x.name}</div>
                  <div className="text-xs text-secondary">{x.module}{x.observed ? ` · Observed: ${x.observed}` : ""}{x.error ? ` · ${x.error}` : ""}</div>
                </div>
                <Badge className="rounded-full text-[10px]" style={{ background: `${c}1F`, color: c }}>{x.status}</Badge>
              </div>
            );
          })}
        </div>
        <p className="mt-8 text-xs text-secondary">AI-Powered Testing. Human-Governed Assurance. · Shared by {r.shared_by} · trinity.platform.specia.ai</p>
      </main>
    </div>
  );
}
